// The in-chat goal card — pure helpers shared by the chat state (use-chat), the BFF history mapping
// (messages API) and the card UI (MessageRow/ChatGoalCard).
//
// The card rides an ASSISTANT reply like `bandCard`, but it is a LIVE view: the metadata names the goal
// and carries a snapshot of the plan for first paint, and everything that moves (status, each task,
// the percent, whether Kick off still shows) comes from the live goals query. The row is never
// rewritten as the goal progresses.
//
// Deliberately dependency-free (mirrors lib/band-cards.ts): the validation MIRRORS common-lib's
// `readGoalCard` and its spec rather than importing it. Keep the two in step.

import type { BandCardTaskData } from './band-cards';
import { CHIP_FAMILIES, type ChipFamily } from './opportunity-card';
import type { WebGoal } from './goals';

export type GoalCardVariant = 'plan' | 'completed';

export type GoalCardData = {
  variant: GoalCardVariant;
  title: string;
  slug?: string;
  goalId?: string;
  value?: string;
  tasks?: BandCardTaskData[];
};

const VARIANTS: readonly GoalCardVariant[] = ['plan', 'completed'];
const TASK_STATUSES: readonly BandCardTaskData['status'][] = ['idle', 'working', 'done'];

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** Read the goal card off a message's metadata, or `null`. The floor: a known variant, a title, and a slug or an id. */
export function readGoalCard(metadata: unknown): GoalCardData | null {
  const card = asRecord(asRecord(metadata)?.goalCard);
  const variant = trimmed(card?.variant) as GoalCardVariant;
  const title = trimmed(card?.title);
  const slug = trimmed(card?.slug);
  const goalId = trimmed(card?.goalId);
  if (!card || !VARIANTS.includes(variant) || !title || (!slug && !goalId)) return null;
  const tasks = (Array.isArray(card.tasks) ? card.tasks : []).flatMap((entry) => {
    const task = asRecord(entry);
    const taskTitle = trimmed(task?.title);
    if (!taskTitle) return [];
    const raw = trimmed(task?.status) as BandCardTaskData['status'];
    const agent = trimmed(task?.agent);
    return [{ title: taskTitle, status: TASK_STATUSES.includes(raw) ? raw : 'idle', ...(agent ? { agent } : {}) }];
  });
  const value = trimmed(card.value);
  return {
    variant,
    title,
    ...(slug ? { slug } : {}),
    ...(goalId ? { goalId } : {}),
    ...(value ? { value } : {}),
    ...(tasks.length > 0 ? { tasks } : {}),
  };
}

/** The value chip, when it names a family the chip vocabulary knows. An unknown id draws no chip. */
export function goalCardChip(card: GoalCardData): ChipFamily | undefined {
  return CHIP_FAMILIES.includes(card.value as ChipFamily) ? (card.value as ChipFamily) : undefined;
}

/** The live goal the card is about: by id first (it cannot collide), else by slug, members included. */
export function findGoalForCard(goals: WebGoal[], card: GoalCardData): WebGoal | undefined {
  const flat = goals.flatMap((goal) => [goal, ...(goal.members ?? [])]);
  return (
    (card.goalId ? flat.find((goal) => goal.id === card.goalId) : undefined) ?? (card.slug ? flat.find((goal) => goal.slug === card.slug) : undefined)
  );
}

/**
 * What the plan card draws.
 *
 * - `kickoff`: the goal is laid out and waiting on the user's go — numbered tasks and the Kick off button.
 * - `running`: anything past KICKOFF — each task's live state, and "See the full plan".
 * - `snapshot`: the live goal is not (or no longer) in the list — the plan as it was laid out, with no
 *   action at all. Kick off must never show over a goal we cannot see: we would be asking the user to
 *   start something that may already be running, or gone.
 */
export type GoalCardPhase = 'kickoff' | 'running' | 'snapshot';

export function goalCardPhase(goal: WebGoal | undefined): GoalCardPhase {
  if (!goal) return 'snapshot';
  return goal.status === 'KICKOFF' ? 'kickoff' : 'running';
}
